import * as Sentry from "@sentry/nextjs"; // Asegúrate de que esta sea la importación correcta para tu setup de Sentry
import {
  isCompanyAccessDeniedError,
  notifyCompanyAccessDenied,
} from "@/utils/errors/companyAccessDenied";

// The request never reached the server (offline, flaky mobile data, blocked request).
// Browsers surface it as a TypeError with an engine-specific message.
const NETWORK_ERROR_MESSAGES = [
  "Failed to fetch", // Chromium
  "Load failed", // Safari
  "NetworkError when attempting to fetch resource", // Firefox
];

export const isNetworkError = (err: Error): boolean =>
  err instanceof TypeError &&
  NETWORK_ERROR_MESSAGES.some((message) => err.message.includes(message));

export const handleHookError = (
  showToast: (props: any) => void,
  err: Error,
  title: string,
  description?: string,
  action?: {
    label: string;
    onClick: () => Promise<{ response: boolean }> | void | undefined;
  },
) => {
  // A deploy happened while this tab was open: the cached client bundle points to a
  // server action id that no longer exists. Expected after each release, so ask for a
  // reload instead of reporting it as a failure.
  if (err.name === "UnrecognizedActionError") {
    showToast({
      title: "A new version is available",
      description: "Reload the page to continue.",
      variant: "destructive",
      action: {
        label: "Reload",
        onClick: () => window.location.reload(),
      },
    });
    return;
  }

  const networkError = isNetworkError(err);

  // Siempre reporta el error a Sentry (network failures as warnings: not a code defect)
  Sentry.captureException(err, {
    level: networkError ? "warning" : undefined,
    tags: networkError ? { errorCategory: "network_error" } : undefined,
    extra: {
      hookTitle: title,
      hookDescription: description,
    },
  });

  if (networkError) {
    showToast({
      title: "Connection problem",
      description:
        "We couldn't reach the server. Check your internet connection and try again.",
      variant: "destructive",
      action: action,
    });
    return;
  }

  if (isCompanyAccessDeniedError(err)) {
    notifyCompanyAccessDenied(err);
    return;
  }

  if (
    err.message.startsWith("Permission denied:") ||
    (err as any).code === "42501"
  ) {
    showToast({
      title: "Unauthorized", // Use the provided title
      description: "You don't have permission to perform this action", // Use the provided description
      variant: "destructive",
    });
  } else {
    showToast({
      title: title,
      description: description,
      variant: "destructive",
      action: action,
    });
  }
};

export const handleActionError = (
  err: any,
  inputValues: any,
  message: string,
) => {
  if (isCompanyAccessDeniedError(err)) {
    throw new Error(err.message || "Access denied for company", {
      cause: {
        originalError: err.message,
        inputValues: JSON.stringify(inputValues),
        errorCode: err.code,
      },
    });
  }

  if (
    err.message.startsWith("Permission denied:") ||
    (err as any).code === "42501"
  ) {
    throw new Error(
      "Permission denied: You don't have the necessary rights to update company info",
      {
        cause: {
          originalError: err.message,
          inputValues: JSON.stringify(inputValues),
          errorCode: err.code,
        },
      },
    );
  } else {
    throw new Error(message, {
      cause: {
        originalError: err.message,
        inputValues: JSON.stringify(inputValues),
        errorCode: err.code,
      },
    });
  }
};
